import { types as eventTypes } from '../enums/event';
import Indicator from '../Indicator';
import subpixel from '../util/canvasSubpixelOffset';

/** High/Low indicator */
export default class BestWorstIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} timeframe - Number of trading days (or trading minutes) to measure
	 * @property {boolean} auto - If true, calculate ranges automatically from timeframe input
	 * @property {string} dateStartBest - Starting date of best period.  Must be parseable by Date.parse()
	 * @property {string} dateStopBest - Ending date of best period.  Must be parseable by Date.parse()
	 * @property {string} dateStartWorst - Starting date of worst period.  Must be parseable by Date.parse()
	 * @property {string} dateStopWorst - Ending date of worst period.  Must be parseable by Date.parse()
	 */
	_getDefaultParams() {
		return {
			id: 'bestworst',
			name: 'Best / Worst',
			description: 'Shade the best/worst performing time range',
			auto: true,
			dateStartBest: null,
			dateStopBest: null,
			dateStartWorst: null,
			dateStopWorst: null,
			timeframe: 20
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return 10000;
	}

	/** @returns {null} */
	getRangeMax() {
		return 10000;
	}

	/** @returns {null} */
	_getMarkers() {
		return {};
	}

	/** @returns {null} */
	_getDataMap() {
		return null;
	}

	/**
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {number} dateStart - Start date of region
	 * @param {number} dateStop - Stop date of region
	 * @param {string} bgColor - Background color of region
	 * @param {string} lineColor - Left/right border color of region
	 * @param {string} lineDash - Line dash
	 * @returns {undefined}
	 */
	_renderRegion(id, dateStart, dateStop, bgColor, lineColor, lineDash) {
		if (dateStart && dateStop) {
			const ctx = this.panel.rootContext;
			const minDate = new Date(dateStart);
			const maxDate = new Date(dateStop);
			const size = this.panel.size;
			const height = size.height - 1.5;
			const { core } = this;
			const x0 = this.xScale(core.closestExchangeIndex(minDate));
			const x1 = Math.min(
				this.xScale(core.closestExchangeIndex(maxDate)),
				size.width
			);
			ctx.beginPath();
			ctx.fillStyle = bgColor;
			ctx.strokeStyle = lineColor;
			ctx.lineWidth = this.params.style.width;
			if (lineDash) ctx.setLineDash(lineDash.split(/\s*,\s*/));
			// fill
			subpixel.fillRect(ctx, x0 + 0.5, 0.5, x1 - x0 - 1, height - 1.5);
			// borders
			subpixel.moveTo(ctx, x0, 0);
			subpixel.lineTo(ctx, x0, height);
			subpixel.moveTo(ctx, x1, 0);
			subpixel.lineTo(ctx, x1, height);
			ctx.stroke();
			ctx.setLineDash([]);
			ctx.closePath();
			// event
			core.eventEmitter.emit(eventTypes.INDICATOR_BESTWORST_UPDATE, {
				id,
				dateStart,
				dateStop
			});
		}
	}

	/** Return start/stop dates for highest and lowest percentage change */
	_getDateRanges(data, timeframe) {
		let maxPct = -Infinity;
		let minPct = Infinity;
		const dates = {
			best: {},
			worst: {}
		};
		for (let i = data.length - 1; i >= timeframe; i--) {
			const thisVal = data[i].close;
			const compareVal = data[i - timeframe].close;
			const pct = (thisVal - compareVal) / Math.abs(compareVal);
			if (pct < minPct) {
				minPct = pct;
				dates.worst.start = data[i - timeframe].date;
				dates.worst.stop = data[i].date;
			} else if (pct > maxPct) {
				maxPct = pct;
				dates.best.start = data[i - timeframe].date;
				dates.best.stop = data[i].date;
			}
		}

		return dates;
	}

	/** Adjust the timeframe input for different periods */
	_getTimeframe(timeframe) {
		const period = this.core.params.dataPeriod;
		if (period === 'Year') {
			return Math.round(timeframe / 20 * 12);
		} else if (period === 'Month') {
			return Math.round(timeframe / 20);
		} else if (period === 'Week') {
			return Math.round(timeframe / 7);
		}

		return timeframe;
	}

	/**
	 * @param {object} data Symbol data object
	 * @returns {undefined}
	 */
	render(data) {
		if (!data.price) return;

		const params = this.params;
		const style = params.style;
		let startBest = params.dateStartBest;
		let stopBest = params.dateStopBest;
		let startWorst = params.dateStartWorst;
		let stopWorst = params.dateStopWorst;
		if (params.auto && params.timeframe) {
			const timeframe = this._getTimeframe(this.params.timeframe);
			const { best, worst } = this._getDateRanges(data.price, timeframe);
			startBest = best.start;
			stopBest = best.stop;
			startWorst = worst.start;
			stopWorst = worst.stop;
		}
		// best region
		this._renderRegion(
			'best',
			startBest,
			stopBest,
			style.best.background.color,
			style.best.color,
			style.best.lineDash
		);
		// worst region
		this._renderRegion(
			'worst',
			startWorst,
			stopWorst,
			style.worst.background.color,
			style.worst.color,
			style.worst.lineDash
		);
	}
}
